import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './Onboarding.css';
import { useGstinLookup } from '../../hooks/useGstinLookup';

const TOTAL_STEPS = 5;

const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

// ── Step progress bar ────────────────────────────────────────────────────────
function ProgressBar({ step }) {
  const pct = ((step - 1) / (TOTAL_STEPS - 1)) * 100;
  return (
    <div className="ob-progress-wrap" role="progressbar" aria-valuenow={step} aria-valuemin={1} aria-valuemax={TOTAL_STEPS} aria-label={`Step ${step} of ${TOTAL_STEPS}`}>
      <div className="ob-progress-bar" style={{ width: `${pct}%` }} />
    </div>
  );
}

// ── Step 1 — Company + GSTIN ─────────────────────────────────────────────────
function Step1({ data, onChange, onNext, onSkip, onGstinBlur }) {
  const [error, setError] = useState('');

  function handleNext() {
    if (!data.companyName.trim()) { setError('Company name is required.'); return; }
    if (data.gstin && !GSTIN_REGEX.test(data.gstin.toUpperCase())) {
      setError('GSTIN format looks incorrect. Expected: 22AAAAA0000A1Z5');
      return;
    }
    setError('');
    onNext();
  }

  return (
    <div className="ob-card">
      <div className="ob-step-label">Step 1 of {TOTAL_STEPS}</div>
      <h1 className="ob-title">Tell us about your business</h1>
      <p className="ob-subtitle">We'll use this to pre-fill your invoices and GST returns.</p>

      <div className="ob-field">
        <label htmlFor="ob-company" className="ob-label">Company name <span aria-hidden="true">*</span></label>
        <input
          id="ob-company"
          type="text"
          className="ob-input"
          placeholder="e.g. Sharma Traders Pvt Ltd"
          value={data.companyName}
          onChange={(e) => { onChange('companyName', e.target.value); setError(''); }}
          autoComplete="organization"
          required
        />
      </div>

      <div className="ob-field">
        <label htmlFor="ob-gstin" className="ob-label">
          GSTIN <span className="ob-optional">(optional)</span>
        </label>
        <input
          id="ob-gstin"
          type="text"
          className="ob-input ob-input-mono"
          placeholder="22AAAAA0000A1Z5"
          value={data.gstin}
          onChange={(e) => { onChange('gstin', e.target.value.toUpperCase()); setError(''); }}
          onBlur={onGstinBlur}
          maxLength={15}
          autoComplete="off"
        />
        <p className="ob-hint">You can add this later in Settings.</p>
      </div>

      {error && <p className="ob-error" role="alert">{error}</p>}

      <div className="ob-actions">
        <button type="button" className="ob-btn-primary" onClick={handleNext}>
          Next →
        </button>
        <button type="button" className="ob-btn-skip" onClick={onSkip}>
          Skip for now
        </button>
      </div>
    </div>
  );
}

// ── Step 2 — Financial year ──────────────────────────────────────────────────
function Step2({ data, onChange, onNext, onBack }) {
  const currentYear = new Date().getFullYear();
  const years = [currentYear - 1, currentYear, currentYear + 1];

  return (
    <div className="ob-card">
      <div className="ob-step-label">Step 2 of {TOTAL_STEPS}</div>
      <h2 className="ob-title">Set your financial year</h2>
      <p className="ob-subtitle">This determines when your invoice numbering resets.</p>

      <div className="ob-field">
        <label htmlFor="ob-fy-start" className="ob-label">Financial year starts in</label>
        <select
          id="ob-fy-start"
          className="ob-input"
          value={data.fyStartMonth}
          onChange={(e) => onChange('fyStartMonth', e.target.value)}
        >
          <option value="April">April (most common — Indian FY)</option>
          <option value="January">January (calendar year)</option>
        </select>
      </div>

      <div className="ob-field">
        <label htmlFor="ob-fy-year" className="ob-label">Current financial year</label>
        <select
          id="ob-fy-year"
          className="ob-input"
          value={data.fyYear}
          onChange={(e) => onChange('fyYear', e.target.value)}
        >
          {years.map((y) => (
            <option key={y} value={y}>{y}–{y + 1}</option>
          ))}
        </select>
      </div>

      <div className="ob-actions">
        <button type="button" className="ob-btn-primary" onClick={onNext}>
          Next →
        </button>
        <button type="button" className="ob-btn-back" onClick={onBack}>
          ← Back
        </button>
      </div>
    </div>
  );
}

// ── Step 3 — First party ─────────────────────────────────────────────────────
function Step3({ data, onChange, onNext, onBack, onSkip, onGstinBlur }) {
  return (
    <div className="ob-card">
      <div className="ob-step-label">Step 3 of {TOTAL_STEPS}</div>
      <h2 className="ob-title">Add your first customer or vendor</h2>
      <p className="ob-subtitle">
        Parties are the customers and vendors you do business with. You can add more later.
      </p>

      <div className="ob-field">
        <label htmlFor="ob-party-name" className="ob-label">Party name</label>
        <input
          id="ob-party-name"
          type="text"
          className="ob-input"
          placeholder="e.g. Rajan Enterprises"
          value={data.partyName}
          onChange={(e) => onChange('partyName', e.target.value)}
          autoComplete="off"
        />
      </div>

      <div className="ob-field">
        <label htmlFor="ob-party-gstin" className="ob-label">
          GSTIN <span className="ob-optional">(optional)</span>
        </label>
        <input
          id="ob-party-gstin"
          type="text"
          className="ob-input ob-input-mono"
          placeholder="22AAAAA0000A1Z5"
          value={data.partyGstin}
          onChange={(e) => onChange('partyGstin', e.target.value.toUpperCase())}
          onBlur={onGstinBlur}
          maxLength={15}
          autoComplete="off"
        />
      </div>

      <div className="ob-field">
        <div className="ob-label" id="ob-party-type-label">Type</div>
        <div className="ob-segment" role="group" aria-labelledby="ob-party-type-label">
          {['Customer', 'Vendor'].map((type) => (
            <button
              key={type}
              type="button"
              className={`ob-segment-btn${data.partyType === type ? ' ob-segment-btn--active' : ''}`}
              onClick={() => onChange('partyType', type)}
              aria-pressed={data.partyType === type}
            >
              {type}
            </button>
          ))}
        </div>
      </div>

      <div className="ob-actions">
        <button type="button" className="ob-btn-primary" onClick={onNext}>
          Next →
        </button>
        <button type="button" className="ob-btn-back" onClick={onBack}>
          ← Back
        </button>
        <button type="button" className="ob-btn-skip" onClick={onSkip}>
          Skip for now
        </button>
      </div>
    </div>
  );
}

// ── Step 4 — First product ───────────────────────────────────────────────────
function Step4({ data, onChange, onNext, onBack, onSkip }) {
  return (
    <div className="ob-card">
      <div className="ob-step-label">Step 4 of {TOTAL_STEPS}</div>
      <h2 className="ob-title">Add your first product or service</h2>
      <p className="ob-subtitle">
        Save your items once — they auto-fill on every invoice.
      </p>

      <div className="ob-field">
        <label htmlFor="ob-product-name" className="ob-label">Product / Service name</label>
        <input
          id="ob-product-name"
          type="text"
          className="ob-input"
          placeholder="e.g. Consulting Services"
          value={data.productName}
          onChange={(e) => onChange('productName', e.target.value)}
          autoComplete="off"
        />
      </div>

      <div className="ob-field">
        <label htmlFor="ob-product-hsn" className="ob-label">
          HSN / SAC code <span className="ob-optional">(optional)</span>
        </label>
        <input
          id="ob-product-hsn"
          type="text"
          className="ob-input ob-input-mono"
          placeholder="e.g. 998314"
          value={data.productHsn}
          onChange={(e) => onChange('productHsn', e.target.value)}
          autoComplete="off"
        />
      </div>

      <div className="ob-field">
        <label htmlFor="ob-product-gst" className="ob-label">GST rate</label>
        <select
          id="ob-product-gst"
          className="ob-input"
          value={data.productGstRate}
          onChange={(e) => onChange('productGstRate', e.target.value)}
        >
          <option value="0">0% (Nil rated / exempt)</option>
          <option value="5">5%</option>
          <option value="12">12%</option>
          <option value="18">18%</option>
          <option value="28">28%</option>
        </select>
      </div>

      <div className="ob-field">
        <label htmlFor="ob-product-price" className="ob-label">
          Unit price (₹) <span className="ob-optional">(optional)</span>
        </label>
        <input
          id="ob-product-price"
          type="number"
          inputMode="decimal"
          className="ob-input"
          placeholder="e.g. 5000"
          value={data.productPrice}
          onChange={(e) => onChange('productPrice', e.target.value)}
          min="0"
          step="0.01"
        />
      </div>

      <div className="ob-actions">
        <button type="button" className="ob-btn-primary" onClick={onNext}>
          Next →
        </button>
        <button type="button" className="ob-btn-back" onClick={onBack}>
          ← Back
        </button>
        <button type="button" className="ob-btn-skip" onClick={onSkip}>
          Skip for now
        </button>
      </div>
    </div>
  );
}

// ── Step 5 — Done! ───────────────────────────────────────────────────────────
function Step5({ companyName, onDone }) {
  const navigate = useNavigate();
  const displayName = companyName.trim().length > 30
    ? companyName.trim().slice(0, 30) + '…'
    : companyName.trim() || 'your business';

  return (
    <div className="ob-card ob-card--done">
      <div className="ob-confetti" aria-hidden="true">🎉</div>
      <h2 className="ob-title ob-title--done">You're all set, {displayName}!</h2>
      <p className="ob-subtitle">
        GST Prime is ready. Create your first invoice or explore the docs to learn more.
      </p>

      <div className="ob-actions ob-actions--done">
        <button
          type="button"
          className="ob-btn-primary"
          onClick={() => { onDone(); navigate('/sales/invoice/new'); }}
        >
          Create your first invoice
        </button>
        <a
          href={`${import.meta.env.REACT_APP_WEBSITE_URL || 'https://www.gstprime.in'}/docs/getting-started`}
          className="ob-btn-link"
          target="_blank"
          rel="noopener noreferrer"
        >
          Read the docs →
        </a>
      </div>
    </div>
  );
}

// ── Main onboarding shell ─────────────────────────────────────────────────────
export default function Onboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    companyName: '',
    gstin: '',
    fyStartMonth: 'April',
    fyYear: String(new Date().getFullYear()),
    partyName: '',
    partyGstin: '',
    partyType: 'Customer',
    productName: '',
    productHsn: '',
    productGstRate: '18',
    productPrice: '',
  });

  const handleChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const { lookupGstin } = useGstinLookup();

  // GSTIN is optional here too — a failed/not-found lookup is a silent no-op.
  // Onboarding has no address fields, so only the name field is auto-filled,
  // and only when the user hasn't already typed one.
  const handleCompanyGstinBlur = useCallback(async () => {
    const gstin = (formData.gstin || '').toUpperCase();
    if (!GSTIN_REGEX.test(gstin)) return;
    const details = await lookupGstin(gstin);
    if (details && !formData.companyName) {
      handleChange('companyName', details.displayName || details.legalName || '');
    }
  }, [formData.gstin, formData.companyName, lookupGstin, handleChange]);

  const handlePartyGstinBlur = useCallback(async () => {
    const gstin = (formData.partyGstin || '').toUpperCase();
    if (!GSTIN_REGEX.test(gstin)) return;
    const details = await lookupGstin(gstin);
    if (details && !formData.partyName) {
      handleChange('partyName', details.displayName || details.legalName || '');
    }
  }, [formData.partyGstin, formData.partyName, lookupGstin, handleChange]);

  function markOnboardingComplete() {
    try { localStorage.setItem('onboardingComplete', '1'); } catch (_e) { /* storage unavailable */ }
  }

  function handleSkip() {
    if (step < TOTAL_STEPS) setStep((s) => s + 1);
    else { markOnboardingComplete(); navigate('/dashboard'); }
  }

  return (
    <div className="ob-shell">
      <div className="ob-inner">
        <div className="ob-logo" aria-label="GST Prime">
          <span className="logo-icon">⚡</span>
          <span>GST Prime</span>
        </div>

        <ProgressBar step={step} />

        {step === 1 && (
          <Step1
            data={formData}
            onChange={handleChange}
            onNext={() => setStep(2)}
            onSkip={handleSkip}
            onGstinBlur={handleCompanyGstinBlur}
          />
        )}
        {step === 2 && (
          <Step2
            data={formData}
            onChange={handleChange}
            onNext={() => setStep(3)}
            onBack={() => setStep(1)}
          />
        )}
        {step === 3 && (
          <Step3
            data={formData}
            onChange={handleChange}
            onNext={() => setStep(4)}
            onBack={() => setStep(2)}
            onSkip={handleSkip}
            onGstinBlur={handlePartyGstinBlur}
          />
        )}
        {step === 4 && (
          <Step4
            data={formData}
            onChange={handleChange}
            onNext={() => setStep(5)}
            onBack={() => setStep(3)}
            onSkip={handleSkip}
          />
        )}
        {step === 5 && (
          <Step5
            companyName={formData.companyName}
            onDone={markOnboardingComplete}
          />
        )}
      </div>
    </div>
  );
}
